import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, RefreshCw, Tags } from 'lucide-react';
import BreadcrumbTwo from '../../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../../../components/DataTable/BasicPagination';
import PageSizeSelector from '../../../../components/DataTable/PageSizeSelector';
import SectionWrapper from '../../../../components/SectionWrapper/SectionWrapper';
import {
  MenuCategoryEmpty,
  MenuCategoryFilters,
  MenuCategoryGrid,
  MenuCategoryHeader,
  MenuCategorySkeleton,
  MenuCategoryTable,
} from '.';
import MenuCategoryFormModal from '../MenuCategoryFormModal';
import useDebounced from '../../../../hooks/useDebounced';
import useRestaurantMenu from '../../../../hooks/restaurant/useRestaurantMenu';
import WorkspaceLayout from '../../../../layout/WorkspaceLayout';
import BranchService from '../../../../services/branch/BranchService';
import { useAuthUser } from '../../../../hooks/auth/useAuthUser';
import { IBranchDropdown } from '../../../../types/branches/branchInterfaces';
import { RestaurantMenuCategoryFilters } from '../../../../types/restaurant/RestaurantMenuTypes';

const RestaurantMenuCategoriesScreen = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [branchError, setBranchError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounced(search, 350);
  const normalizedSearch = search === '' ? '' : debouncedSearch;
  const menu = useRestaurantMenu(branchId);

  useEffect(() => {
    menu.resetCategoryResults();
    return menu.resetCategoryResults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setBranchError('');
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((data) => {
        setBranches(data);
        if (data.length) setBranchId(data[0].id);
      })
      .catch((error: any) =>
        setBranchError(
          error?.response?.data?.message ||
            'No fue posible cargar las sucursales.'
        )
      )
      .finally(() => setLoadingBranches(false));
  }, [canPickBranch]);

  const filters = useMemo<RestaurantMenuCategoryFilters>(
    () => ({
      search: normalizedSearch.trim() || null,
      active: status === 'ALL' ? null : status === 'ACTIVE',
      page,
      size: pageSize,
    }),
    [normalizedSearch, page, pageSize, status]
  );

  useEffect(() => {
    if (branchId) menu.loadCategories(filters, menu.categoryPage !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, filters]);

  const changeBranch = (id: number) => {
    menu.resetCategoryResults();
    setSearch('');
    setStatus('ALL');
    setPage(0);
    setBranchId(id);
  };
  const previousPage = () => setPage((current) => Math.max(0, current - 1));
  const retry = () => menu.loadCategories(filters, menu.categoryPage !== null);

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />
      <SectionWrapper>
        <MenuCategoryHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          branchError={branchError}
          canPickBranch={canPickBranch}
          onCreate={menu.openCategory}
          onBranchChange={changeBranch}
        />

        {!loadingBranches && !branchError && branches.length === 0 ? (
          <div className="p-14 text-center">
            <Tags className="mx-auto h-10 w-10 text-bodydark2" />
            <h2 className="mt-3 font-semibold text-black dark:text-white">
              No hay sucursales disponibles
            </h2>
          </div>
        ) : branchId ? (
          <>
            <MenuCategoryFilters
              search={search}
              status={status}
              onSearchChange={(value) => {
                setSearch(value);
                setPage(0);
              }}
              onStatusChange={(value) => {
                setStatus(value);
                setPage(0);
              }}
            />

            {menu.loading ? (
              <MenuCategorySkeleton />
            ) : menu.error && !menu.categoryPage ? (
              <div className="flex min-h-[300px] flex-col items-center justify-center px-6 text-center">
                <span className="mb-4 rounded-full bg-danger/10 p-4 text-danger">
                  <AlertCircle className="h-8 w-8" />
                </span>
                <h3 className="text-lg font-semibold text-black dark:text-white">
                  No fue posible cargar las categorías
                </h3>
                <p className="mt-2 text-sm text-bodydark2">{menu.error}</p>
                <button
                  type="button"
                  onClick={retry}
                  className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-medium text-white"
                >
                  <RefreshCw className="h-4 w-4" /> Reintentar
                </button>
              </div>
            ) : menu.categories.length === 0 ? (
              <MenuCategoryEmpty />
            ) : (
              <div className="relative">
                {menu.error && (
                  <div className="m-5 flex items-center justify-between gap-3 rounded-lg border border-danger/20 bg-danger/5 px-4 py-3 text-sm text-danger">
                    <span>{menu.error}</span>
                    <button
                      type="button"
                      onClick={retry}
                      className="font-semibold underline"
                    >
                      Reintentar
                    </button>
                  </div>
                )}
                {menu.updating && (
                  <div className="absolute right-5 top-3 z-10 flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-primary shadow dark:bg-boxdark">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />{' '}
                    Actualizando
                  </div>
                )}
                <MenuCategoryTable
                  categories={menu.categories}
                  onEdit={menu.editCategory}
                  onToggle={(category) =>
                    menu.toggleCategory(category, filters, previousPage)
                  }
                />
                <MenuCategoryGrid
                  categories={menu.categories}
                  onEdit={menu.editCategory}
                  onToggle={(category) =>
                    menu.toggleCategory(category, filters, previousPage)
                  }
                />
              </div>
            )}

            {menu.categoryPage && menu.categoryPage.page.totalElements > 0 && (
              <div className="flex flex-col gap-4 border-t border-stroke px-5 pb-5 dark:border-strokedark sm:flex-row sm:items-end sm:justify-between">
                <p className="pb-2 text-sm font-medium text-bodydark2">
                  {menu.categoryPage.page.totalElements.toLocaleString('es-SV')}{' '}
                  categorías encontradas
                </p>
                <BasicPagination
                  currentPage={menu.categoryPage.page.number}
                  totalPages={menu.categoryPage.page.totalPages}
                  onPageChange={setPage}
                />
                <PageSizeSelector
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(0);
                  }}
                />
              </div>
            )}
          </>
        ) : null}
      </SectionWrapper>

      <MenuCategoryFormModal
        isOpen={menu.categoryModalOpen}
        form={menu.categoryForm}
        saving={menu.saving}
        onClose={menu.closeCategory}
        onChange={menu.updateCategoryField}
        onSave={() => menu.saveCategory(filters, previousPage)}
      />
    </WorkspaceLayout>
  );
};

export default RestaurantMenuCategoriesScreen;
